<template>
    <section v-if="items.length">
        <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <SectionHeader title="Tickets" prefix="Event!" :icon="Ticket" :options="layoutOptions" v-model="mode" />

            <template v-if="mode === 'wizard'">
                <div class="w-full md:w-9/12 max-w-max md:max-w-[700px] mx-auto">
                    <WizardView
                        :rows="ticketRows"
                        :max-qty="maxQty"
                        :form="form"
                        :currency="currency"
                        :step="step"
                        :error="error"
                        :reference="reference"
                        :order-id="orderId"
                        @update:qty="setQty"
                        @update:holder="setHolderByKey"
                        @submit="submit"
                        @reset="reset"
                        @paid="onPaid" />
                </div>
            </template>
            <template v-else>
                <StandardView v-if="mode === 'standard'" :rows="ticketRows" :max-qty="maxQty" @update:qty="setQty" />
                <SimpleView v-else :rows="ticketRows" :max-qty="maxQty" @update:qty="setQty" />

                <TicketCheckout v-if="selectedLines.length"
                    :lines="checkoutLines"
                    :form="form"
                    :currency="currency"
                    :step="step"
                    :error="error"
                    :reference="reference"
                    :order-id="orderId"
                    @update:holder="setHolderByKey"
                    @submit="submit"
                    @reset="reset"
                    @paid="onPaid" />
            </template>
            <!-- <div class="mt-6 p-6 rounded-lg bg-red-500/20">{{ form }}</div> -->
        </div>
    </section>
</template>

<script setup lang="ts">
import { ref, reactive, computed, watch } from 'vue';
import { Ticket, LayoutGrid, List, ClipboardList } from 'lucide-vue-next';
import { EventItem, EventTicketType, Order, TicketHolder } from '~/types';
import { submitEventCheckout } from '~/utils/api';
import SectionHeader from './SectionHeader.vue';
import StandardView from './EventTickets/StandardView.vue';
import SimpleView from './EventTickets/SimpleView.vue';
import WizardView from './EventTickets/WizardView.vue';
import TicketCheckout from './EventTickets/TicketCheckout.vue';

type TicketLayout = 'standard' | 'simple' | 'wizard';

const props = withDefaults(
    defineProps<{ items: EventTicketType[]; event: EventItem | null; variant?: TicketLayout }>(),
    { variant: 'standard' }
);
const emit = defineEmits<{ (e: 'success', payload: Order & { reference: string }): void }>();

const mode = ref<TicketLayout>(props.variant);
const layoutOptions: { value: TicketLayout; label: string; icon: any }[] = [
    { value: 'standard', label: 'Standard', icon: LayoutGrid },
    { value: 'simple', label: 'Simple', icon: List },
    { value: 'wizard', label: 'Registration wizard', icon: ClipboardList },
];

const maxQty = 10;

const step = ref<'form' | 'processing' | 'payment' | 'done'>('form');
const error = ref('');
const reference = ref('');
const orderId = ref('');

const isSoldOut = (t: EventTicketType) => t.soldOut === true || t.available === false;

const form = reactive<Order>({
    eventId: '',
    userId: null,
    firstName: '',
    secondName: '',
    lastName: '',
    email: '',
    phone: '',
    amount: 0,
    serviceFee: 0,
    orderItems: [],
});

const ticketKey = (t: EventTicketType) => t.id || t.title;

// Optional holder details, one entry per ticket, keyed by ticketKey.
const emptyHolder = (): TicketHolder => ({ firstName: '', secondName: '', lastName: '', email: '', phone: '' });
const holders = reactive<Record<string, TicketHolder[]>>({});
const setHolderByKey = (key: string, index: number, value: TicketHolder) => {
    try {
        if(holders[key]) {
            holders[key][index] = value;
        }
    } catch (error) {
        console.log("setHolderByKey", error)        
    }

};

// Quantity per ticket type, keyed by ticketKey.
const quantities = reactive<Record<string, number>>({});
const qty = (t: EventTicketType) => quantities[ticketKey(t)] || 0;
const setQty = (t: EventTicketType, n: number) => {
    const key = ticketKey(t);
    const quantity = Math.min(maxQty, Math.max(0, n));
    quantities[key] = quantity;

    // Grow/shrink the holder list so there is one optional entry per ticket.
    const list = (holders[key] ??= []);
    while (list.length < quantity) list.push(emptyHolder());
    list.length = quantity;
};

const hasDetails = (h: TicketHolder) => !!(h.firstName || h.lastName || h.email || h.phone);

// All tickets paired with their current quantity, for the Standard/Simple views.
const ticketRows = computed(() => props.items.map((t) => ({ ticket: t, quantity: qty(t) })));

const selectedLines = computed(() =>
    props.items
        .filter((t) => !isSoldOut(t) && qty(t) > 0)
        .map((t) => ({ key: ticketKey(t), ticket: t, quantity: qty(t) }))
);

// Same lines, enriched with each ticket's holder list, for TicketCheckout.
const checkoutLines = computed(() =>
    selectedLines.value.map((line) => ({ ...line, holders: holders[line.key] || [] }))
);

const currency = computed(() => props.items[0]?.currency || 'USD');

// Keep the Order's line items and amount in sync with the chosen quantities.
watch(
    [selectedLines, holders, () => props.event],
    () => {
        form.eventId = props.event?.id || '';
        form.orderItems = selectedLines.value.map(({ key, ticket, quantity }) => {
            // Only send holders the buyer actually filled in.
            const ticketHolders = (holders[key] || []).filter(hasDetails).map((h) => ({ ...h }));
            const h = ticketHolders[0];

            return {
                eventId: form.eventId,
                ticketTypeId: ticket.id || '',
                firstName: h?.firstName || null,
                secondName: h?.secondName || null,
                lastName: h?.lastName || null,
                email: h?.email || null,
                phone: h?.phone || null,
                price: ticket.price || 0,
                quantity,
                ticketHolders,
            };
        });
        form.amount =
            form.orderItems.reduce((sum, i) => sum + i.price * i.quantity, 0) + form.serviceFee;
    },
    { immediate: true, deep: true }
);

const genReference = () =>
    'EVT-' + Math.random().toString(36).slice(2, 6).toUpperCase() + '-' + Date.now().toString().slice(-4);

const submit = async () => {
    if (!props.event || !form.orderItems.length) return;
    error.value = '';
    step.value = 'processing';

    try {
        const res = await submitEventCheckout({ ...form, eventSlug: props.event.slug, currency: currency.value });
        if (!res || res.status === false) {
            throw new Error((res && res.message) || 'Checkout failed');
        }
        console.log("res.data", res)
        orderId.value = res?.id || '';
        reference.value = orderId.value || genReference();
    } catch (e: any) {
        console.error('Event checkout failed', e);
        error.value = e?.message || 'We could not complete your order. Please try again.';
        step.value = 'form';
        return;
    }

    // Free order — nothing to pay, skip straight to confirmation.
    if (form.amount <= 0) {
        onPaid();
        return;
    }

    // Order created (unpaid) — hand off to the payment step.
    step.value = 'payment';
};

// Called once PaymentPanel confirms the gateway settled the payment.
const onPaid = () => {
    step.value = 'done';
    emit('success', { ...form, reference: reference.value });
};

const reset = () => {
    step.value = 'form';
    error.value = '';
    reference.value = '';
    orderId.value = '';
    Object.keys(quantities).forEach((k) => delete quantities[k]);
    Object.keys(holders).forEach((k) => delete holders[k]);
    form.firstName = '';
    form.lastName = '';
    form.email = '';
    form.phone = '';
};
</script>
